import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';

interface VideoSession {
  sessionId: number;
  bookingId: number;
  roomName: string;
  roomUrl: string;
  status: 'WAITING' | 'IN_PROGRESS' | 'ENDED';
  startedAt?: string;
  endedAt?: string;
  durationMinutes?: number;
}

interface JoinSessionResponse {
  token: string;
  roomUrl: string;
  sessionId: number;
}

interface CreateRoomRequest {
  bookingId: number;
}

/**
 * Hook for creating a video room for a booking (therapist only)
 */
export const useCreateRoom = () => {
  const queryClient = useQueryClient();

  return useMutation<VideoSession, Error, CreateRoomRequest>({
    mutationFn: async ({ bookingId }) => {
      const response = await apiClient.post('/video/rooms', { bookingId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videoSessions'] });
    },
  });
};

/**
 * Hook for fetching video room information
 */
export const useVideoRoom = (bookingId: number | null) => {
  return useQuery<VideoSession, Error>({
    queryKey: ['videoSession', bookingId],
    queryFn: async () => {
      if (!bookingId) throw new Error('Booking ID is required');
      const response = await apiClient.get(`/video/rooms/${bookingId}`);
      return response.data;
    },
    enabled: !!bookingId,
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchInterval: 10000, // Refetch every 10 seconds to check status
  });
};

/**
 * Hook for joining a video session and getting access token
 */
export const useJoinSession = () => {
  return useMutation<JoinSessionResponse, Error, number>({
    mutationFn: async (bookingId) => {
      const response = await apiClient.post(`/video/rooms/${bookingId}/join`);
      return response.data;
    },
  });
};

/**
 * Hook for ending a video session (therapist only)
 */
export const useEndSession = () => {
  const queryClient = useQueryClient();

  return useMutation<VideoSession, Error, number>({
    mutationFn: async (bookingId) => {
      const response = await apiClient.post(`/video/rooms/${bookingId}/end`);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['videoSession', data.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
};

/**
 * Hook for polling session status
 * Useful for waiting rooms and status updates
 */
export const useSessionStatus = (bookingId: number | null, enabled: boolean = true) => {
  return useQuery<VideoSession, Error>({
    queryKey: ['videoSessionStatus', bookingId],
    queryFn: async () => {
      if (!bookingId) throw new Error('Booking ID is required');
      const response = await apiClient.get(`/video/rooms/${bookingId}`);
      return response.data;
    },
    enabled: enabled && !!bookingId,
    refetchInterval: 5000, // Poll every 5 seconds
    staleTime: 0, // Always consider data stale
  });
};

/**
 * Check if a video room exists for a booking
 */
export const useVideoRoomExists = (bookingId: number | null) => {
  return useQuery<boolean, Error>({
    queryKey: ['videoRoomExists', bookingId],
    queryFn: async () => {
      if (!bookingId) return false;
      try {
        await apiClient.get(`/video/rooms/${bookingId}`);
        return true;
      } catch (error: any) {
        if (error.response?.status === 404) {
          return false;
        }
        throw error;
      }
    },
    enabled: !!bookingId,
    staleTime: 60000, // Cache for 1 minute
  });
};
